/**
 * Alquileres para eventos.
 *
 * El ciclo es corto y siempre el mismo: se prepara, se confirma, se lleva,
 * se recoge. Cada paso mueve también el estado de los extintores, y por eso
 * los botones no son un adorno: entregar los marca como `ALQUILADO` y
 * recoger los devuelve al parque. Si el estado del equipo no siguiera al del
 * alquiler, el parque diría que están libres mientras están en una feria.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { openDownload } from '../session/open-download.js';

interface Rental {
  id: string;
  code: string;
  event_name: string;
  customer_name: string;
  starts_on: string;
  ends_on: string;
  status: string;
  units: string;
  delivery_fee: string;
  certification_fee: string;
}

interface QuoteLine {
  internalCode: string;
  pricePerDay: string;
  days: number;
  amount: string;
}

interface Quote {
  empty: boolean;
  code: string;
  days?: number;
  lines: QuoteLine[];
  rentalTotal?: string;
  deliveryFee?: string;
  certificationFee?: string;
  total?: string;
}

/** El paso siguiente de cada estado, que es el único que se ofrece. */
const SIGUIENTE: Record<string, { to: string; label: string } | undefined> = {
  BORRADOR: { to: 'CONFIRMADO', label: 'Confirmar' },
  CONFIRMADO: { to: 'ENTREGADO', label: 'Marcar entregado' },
  ENTREGADO: { to: 'RECOGIDO', label: 'Marcar recogido' },
};

const TONO: Record<string, string> = {
  BORRADOR: 'info',
  CONFIRMADO: 'warn',
  ENTREGADO: 'bad',
  RECOGIDO: 'ok',
  FACTURADO: 'ok',
  ANULADO: 'off',
};

function eur(value: string | undefined): string {
  if (value === undefined) return '—';
  const [entero = '0', dec = '00'] = value.split('.');
  return `${entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec.padEnd(2, '0').slice(0, 2)} €`;
}

export function RentalsScreen(): React.ReactNode {
  const { api, user } = useSession();
  const canWrite = hasPermission(user, 'sales.update');

  const [items, setItems] = useState<Rental[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ items: Rental[] }>('/rentals');
      setItems(data.items);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se han podido cargar los alquileres.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function avanzar(rental: Rental, to: string, label: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await api.post(`/rentals/${rental.id}/status`, { to });
      setNotice(`${rental.code}: ${label.toLowerCase()}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido cambiar el estado.');
    } finally {
      setBusy(false);
    }
  }

  async function verImporte(rental: Rental): Promise<void> {
    setError(null);
    try {
      setQuote(await api.get<Quote>(`/rentals/${rental.id}/quote`));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido calcular el importe.');
    }
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Alquileres</h1>
        <span className="screen__count">{items.length}</span>
      </header>

      <p className="screen__note">
        Extintores para eventos: se llevan, se certifican y se recogen. Se facturan por unidad y
        día, con el mínimo de cada tarifa; los portes y la certificación van aparte.
      </p>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      <div className="scroller">
        <table className="table__grid">
          <thead>
            <tr>
              <th>Código</th>
              <th>Evento</th>
              <th>Cliente</th>
              <th>Fechas</th>
              <th className="table__num">Unidades</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const paso = SIGUIENTE[r.status];
              return (
                <tr key={r.id}>
                  <td>{r.code}</td>
                  <td>{r.event_name}</td>
                  <td>{r.customer_name}</td>
                  <td>
                    {r.starts_on} → {r.ends_on}
                  </td>
                  <td className="table__num">{r.units}</td>
                  <td>
                    <span className={`pill pill--${TONO[r.status] ?? 'info'}`}>{r.status}</span>
                  </td>
                  <td>
                    <button
                      className="button button--quiet"
                      type="button"
                      onClick={() => void verImporte(r)}
                    >
                      Importe
                    </button>{' '}
                    {/*
                      El certificado sólo se ofrece a partir de CONFIRMADO:
                      un borrador puede cambiar de extintores, y un
                      certificado que no coincide con lo que hay montado es
                      peor que no tenerlo. El servidor lo rechaza igual; esto
                      evita el clic inútil.
                    */}
                    {r.status !== 'BORRADOR' && r.status !== 'ANULADO' && (
                      <>
                        <button
                          className="button button--quiet"
                          type="button"
                          onClick={() =>
                            void openDownload(api, `/v1/rentals/${r.id}/certificate`)
                          }
                        >
                          Certificado
                        </button>{' '}
                      </>
                    )}
                    {canWrite && paso && (
                      <button
                        className="button button--quiet"
                        type="button"
                        disabled={busy}
                        onClick={() => void avanzar(r, paso.to, paso.label)}
                      >
                        {paso.label}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="table__empty">
                  Ningún alquiler todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {quote !== null && (
        <div className="card radar">
          <h2>Importe de {quote.code}</h2>
          {quote.empty ? (
            <p className="table__empty">Este alquiler no tiene extintores asignados.</p>
          ) : (
            <>
              {/* Los días facturados pueden ser más que los del evento: es el
                  mínimo de la tarifa, y verlo evita la llamada del cliente
                  preguntando por qué. */}
              <p className="field__help">
                El evento dura {quote.days} días. Si alguna línea factura más, es el mínimo de su
                tarifa.
              </p>
              <table className="table__grid">
                <thead>
                  <tr>
                    <th>Extintor</th>
                    <th className="table__num">€/día</th>
                    <th className="table__num">Días</th>
                    <th className="table__num">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.lines.map((l) => (
                    <tr key={l.internalCode}>
                      <td>{l.internalCode}</td>
                      <td className="table__num">{eur(l.pricePerDay)}</td>
                      <td className="table__num">
                        {l.days}
                        {quote.days !== undefined && l.days > quote.days ? ' (mínimo)' : ''}
                      </td>
                      <td className="table__num">{eur(l.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3}>Alquiler</td>
                    <td className="table__num">{eur(quote.rentalTotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3}>Portes</td>
                    <td className="table__num">{eur(quote.deliveryFee)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3}>Certificación</td>
                    <td className="table__num">{eur(quote.certificationFee)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3}>
                      <strong>Total (sin IVA)</strong>
                    </td>
                    <td className="table__num">
                      <strong>{eur(quote.total)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </section>
  );
}
